import { cx } from "@emotion/css";
import { Avatar, Box, Button, ListItem, ListItemAvatar, ListItemText, Stack } from "@mui/material";
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, KEYBOARD_ARROW_DOWN, SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcBoxDrawer, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { TextFieldFilter } from "components/MprcAdvFilter";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { UserItemDto } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { RootPaths } from "routes/RootRouter";

type ItemData = {
    items: UserItemDto[];
    selected: string | null;
    onSelectedUser: (userId: string) => void;
};

/**
 * Renderiza uma linha da Lista.
 * @param props
 */
// Renderiza uma linha da Lista.
function RenderRow(props: ListChildComponentProps<ItemData>) {
    const { index, style } = props;
    const { items, onSelectedUser, selected } = props.data;

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];
    return (
        <div key={index} style={style}>
            <ListItem
                disableGutters
                onClick={() => onSelectedUser(item.userId)} className={AppGlobalClassKeys.clickable}
                sx={(theme) => ({ background: selected === item.userId ? theme.palette.highlight.main : undefined })}
            >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                        src={item.thumbnailUrl}
                        sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                    />
                </ListItemAvatar>
                <ListItemText
                    primary={item.name}
                    title={item.name}
                    primaryTypographyProps={{ noWrap: true, variant: "h6" }}
                />
            </ListItem>
        </div>
    );
}

const MemoizedRenderRow = React.memo(RenderRow);

////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    onClose: () => void;
}

export const SpyLoginRequestDialog = (props: IProps) => {
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const { onClose } = props;
    const { t } = useTranslation();
    const session = useSession();
    const navigate = useNavigate();

    const [error, setError] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [items, setItems] = useState<UserItemDto[]>([]);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);

    const currentPage = useRef<number>(1);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);

    let searchTextRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController>(undefined);

    ///
    async function loadData() {
        setLoading(true);
        setError(null);
        abortControllerRef.current = new AbortController();

        try {

            var result = await api.v1.selectors.users({
                searchText: searchText,
                ignoreSelectionHistory: true,
                groupTypes: null,
                pageNumber: currentPage.current,
                pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE,
            }, { signal: abortControllerRef.current.signal });

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.data.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.data.items]);
            }

            // Há mais registros?
            endOfRecords.current = result.data.items.length < DRAWER_INFINITE_SCROLL_PAGE_SIZE;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setError(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
            abortControllerRef.current = undefined;
        }
    }

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = (ignoreHistory: boolean = false) => {
        currentPage.current = 1;
        loadData();
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    ////////////////////////////////////////////////////////////////////////////////

    const onSelected = async () => {
        setSaving(true);

        try {
            const response = await api.v1.user.requestSupportAuthorization({ userId: selectedUser! });
            if (response.data != null) {
                session.finalizeSession(() => navigate(RootPaths.BuildSpyLogin(response.data), { replace: true }));
            }
            else {
                enqueueSnackbar(t("User.SpyLoginRequestDialog.ErrorMessage"), { variant: "error" });
            }
            onClose();
        }
        catch (error) {
            MprcRequestSnackbarErrorReport(error);
        }
        finally {
            setSaving(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////


    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {

        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            resetSearch();
        }, hasMountedRef.current ? SEARCH_DELAY_DEBOUNCE : 0);

        hasMountedRef.current = true;

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcBoxDrawer
            onClose={() => onClose()}
            title={t("User.SpyLoginRequestDialog.Title")}
            header={
                <TextFieldFilter
                    autoFocus
                    fullWidth
                    autoComplete="off"
                    placeholder={t("User.SpyLoginRequestDialog.SelectedUserText")}
                    value={searchText}
                    onChange={() => {
                        setSearchText(searchTextRef.current?.value ?? "");
                    }}
                    onKeyDown={(e) => {
                        if (e.keyCode == 13) resetSearch();
                        else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) resetSearch(true);
                    }}
                    inputRef={searchTextRef}
                    slotProps={{
                        input: {
                            endAdornment:
                                searchText != "" &&
                                <IconButtonClearSearch
                                    size="small"
                                    onClick={() => setSearchText("")}
                                />
                        }
                    }}
                />
            }
            content={
                <>
                    {
                        error
                            ? <MprcRequestErrorReport error={error} />
                            : loading
                                ? <MprcSpinnerIndicator />
                                : !loading && itemCount == 0
                                    ? <MprcFilterEmptyDataset message={t("User.ListUsersDrawer.NoRecordsFoundMessage")} />
                                    : <MprcAutoSizer>
                                        {
                                            ({ width, height }) =>
                                                <InfiniteLoader
                                                    isItemLoaded={isItemLoaded}
                                                    itemCount={itemCount}
                                                    loadMoreItems={loadMoreItems}
                                                    ref={infiniteLoaderRef}
                                                    threshold={10}
                                                    minimumBatchSize={10}
                                                >
                                                    {({ onItemsRendered, ref }) => (
                                                        <FixedSizeList
                                                            height={height}
                                                            width={width}
                                                            itemData={{ items: items, onSelectedUser: (userId) => setSelectedUser(userId), selected: selectedUser }}
                                                            itemCount={itemCount}
                                                            itemSize={40}
                                                            onItemsRendered={onItemsRendered}
                                                            ref={ref}
                                                            className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                                        >
                                                            {MemoizedRenderRow}
                                                        </FixedSizeList>
                                                    )}
                                                </InfiniteLoader>
                                        }
                                    </MprcAutoSizer>
                    }
                </>
            }
            footer={
                <Stack direction="row" spacing={2}>
                    <Button
                        disabled={selectedUser == null || saving}
                        variant="contained"
                        onClick={onSelected}
                    >
                        <span>{t("Auth.Login.SubmitText")}</span>
                        {saving && <MprcSpinnerIndicator />}
                    </Button>

                    <ButtonCancel
                        variant="contained"
                        onClick={onClose}
                    />
                </Stack>
            }
        />
    );
};
